import { ChangeEventWorkflow, SaveChangeEventInput } from "src/generated/graphqlTypes";
import { useDeleteChangeEventMutation } from "src/routes/projects/change-events/ChangeEventPage.generated";
import { GuidedChangeEventStep } from "src/routes/projects/change-events/guided/utils";
import {
  WorkflowActionArgs,
  WorkflowActions,
  WorkflowPageActionArgs,
} from "src/routes/projects/change-events/guided/workflows/types";
import { useGuidedChangeEventActions } from "../sharedSteps/useGuidedChangeEventActions";
import { ApplyConfigurationFormInput } from "./applyConfigurationForm";

/**
 * Saves the apply configuration change event.
 *
 * A config change's `target` can't be updated on BE (its composed `result` would describe the old config), so
 * picking a different configuration on an already-saved draft deletes that draft and creates a new one.
 */
export function useSaveApplyConfigurationChangeEvent(args: WorkflowActionArgs<ApplyConfigurationFormInput>) {
  const { form } = args;
  const [deleteChangeEvent] = useDeleteChangeEventMutation();
  const { saveChangeEvent: saveGuidedChangeEvent } = useGuidedChangeEventActions(args, applyConfigurationSaveInput);

  async function saveChangeEvent(opts?: { andExit?: boolean; dryRun?: boolean }): Promise<void> {
    // Delete first, then create
    if (form.id.value && form.targetConfigId.dirty && form.canSave()) {
      await deleteChangeEvent({ variables: { input: { id: form.id.value } } });
      // `targetConfigId` stays dirty, so the save below still goes through, as a create
      form.set({ id: undefined, configChangeId: undefined });
    }
    // Select Changes' picks are local-only, so a flagged form can be clean; force the save, or it'd skip the re-derive
    return saveGuidedChangeEvent({ ...opts, force: !!form.needsReconcile.value });
  }

  return { saveChangeEvent };
}

/** The config change intent an apply-configuration change event is saved with. */
export function applyConfigurationSaveInput(
  form: WorkflowActionArgs<ApplyConfigurationFormInput>["form"],
): Partial<SaveChangeEventInput> {
  // Sending `configChange` is what re-derives the change event from the picks Select Changes saved onto `result`
  return { configChange: { id: form.configChangeId.value ?? undefined, targetId: form.targetConfigId.value } };
}

/** The actions the initial and partial apply-configuration workflows share. */
export function useApplyConfigurationActions(
  args: WorkflowPageActionArgs<ApplyConfigurationFormInput>,
): WorkflowActions {
  const { form, project } = args;
  const { saveChangeEvent } = useSaveApplyConfigurationChangeEvent(args);
  return {
    // Each workflow overrides this with the commitment cleanup gate.
    onSubmit: async () => {},
    onContinue: {
      [GuidedChangeEventStep.SelectConfiguration]: async () => {
        // With no signed contract the initial configuration is applied directly later, so there is no draft to
        // create; the save is only a dry run, so the Review Impact / Review steps preview the real derivation from the
        // cache. Partial is only offered on projects with a signed contract (`Project.canApplyConfigChange`),
        // so it always saves.
        const isInitial = form.workflow.value === ChangeEventWorkflow.ApplyInitialConfiguration;
        await saveChangeEvent({ dryRun: isInitial && !project.latestActiveStage?.hasSignedContract });
      },
      // The picks are already on `result`, so this re-derives the change event from them
      [GuidedChangeEventStep.SelectChanges]: () => saveChangeEvent(),
    },
    onSaveAndExit: form.value.id ? () => saveChangeEvent({ andExit: true }) : undefined,
  };
}
